// Thin typed wrappers for /api/v1. The dev server proxies /api → :8092
// and injects X-AV-Tenant-ID + X-AV-User-Login headers so the backend's
// requireTenant middleware passes. In production a real session cookie
// (Phase 2 auth) replaces the header injection.
//
// All transport (fetch + credentials + error normalization) is delegated
// to @avagency/frontend/lib/api.apiFetch. This file owns ONLY the typed
// resource interfaces + URL templates.

import { apiFetch, ApiError } from '@avagency/frontend/lib/api';

// ─── Dashboard ──────────────────────────────────────────────────────

export interface DashboardMetrics {
  // SKU counters
  total_skus: number;
  oos_skus: number;
  unconfigured_skus: number;
  // PO status counters
  draft_pos: number;
  sent_pos: number;
  confirmed_pos: number;
  received_pos: number;
  cancelled_pos: number;
  // Billing
  pos_this_month: number;
  monthly_po_cap: number; // -1 = unlimited (Growth / trial)
  billing_tier: 'trial' | 'starter' | 'growth';
  trial_active: boolean;
}

export interface DashboardResponse {
  metrics: DashboardMetrics;
}

// ─── Products ───────────────────────────────────────────────────────

export interface Product {
  id: string;
  integration_id: string;
  sku: string;
  title: string;
  on_hand: number;
  lead_time_days: number;
  reorder_point?: number;
  reorder_qty?: number;
  cost?: number;
  currency: string;
  active: boolean;
  velocity_7d: number;
  velocity_30d: number;
  supplier_id?: string;
  supplier_name?: string;
  last_variant_synced_at?: string;
  last_velocity_at?: string;
}

export interface ProductResponse {
  products: Product[];
}

// ─── Inventory ──────────────────────────────────────────────────────

export interface InventoryItem {
  integration_id: string;
  sku: string;
  title?: string;
  on_hand: number;
  velocity_7d: number;
  velocity_30d: number;
  lead_time_days: number;
  reorder_point?: number;
  reorder_qty?: number;
  supplier_id?: string;
  supplier_name?: string;
  last_synced_at?: string;
  last_velocity_at?: string;
  is_configured: boolean;
}

export interface InventoryResponse {
  items: InventoryItem[];
}

// ─── Suppliers ──────────────────────────────────────────────────────

export interface Supplier {
  id: string;
  integration_id?: string; // absent = tenant-wide
  name: string;
  email: string;
  default_payment_terms: string;
  default_currency: string;
  performance_score?: number | null;
  created_at: string;
  updated_at?: string;
}

export interface SupplierResponse {
  suppliers: Supplier[];
}

// ─── Purchase Orders ────────────────────────────────────────────────

export type POStatus = 'draft' | 'sent' | 'confirmed' | 'received' | 'cancelled';

export interface POSummary {
  id: string;
  integration_id: string;
  supplier_id: string;
  supplier_name?: string;
  po_number: string;
  status: POStatus;
  total_cost: number;
  currency: string;
  line_item_count: number;
  expected_arrival?: string;
  created_at: string;
  sent_at?: string;
  confirmed_at?: string;
  received_at?: string;
  cancelled_at?: string;
}

export interface POLineItem {
  sku: string;
  title?: string;
  qty: number;
  unit_cost: number;
  line_total: number;
  supplier_sku?: string;
}

export interface PODetail extends POSummary {
  line_items: POLineItem[];
  pdf_object_key?: string;
}

export interface POListResponse {
  purchase_orders: POSummary[];
}

export interface PODetailResponse {
  purchase_order: PODetail;
}

// ─── Supplier write shapes ───────────────────────────────────────────

export interface SupplierCreateInput {
  name: string;
  email: string;
  default_payment_terms?: string;
  default_currency?: string;
  integration_id?: string;
}

export interface SupplierUpdateInput {
  name?: string;
  email?: string;
  default_payment_terms?: string;
  default_currency?: string;
}

// ─── URL helpers ──────────────────────────────────────────────────────

// buildQuery preserves the original wrappers' truthy-filter semantics:
// undefined, empty string, 0, and false are all OMITTED.
//
// This matters because:
// - active_only=false would tell the backend to include archived rows
//   (caller intent is "default", not "false")
// - limit=0 could be backend-special (no limit, or strict zero) — better
//   to omit and let the backend default kick in
// - Original wrappers used `if (params?.x) ...` which dropped all falsy
//   values; this preserves that contract exactly.
function buildQuery(params: Record<string, string | number | boolean | undefined>): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v) q.set(k, String(v));
  }
  const qs = q.toString();
  return qs ? `?${qs}` : '';
}

// ─── Dashboard ──────────────────────────────────────────────────────

export const fetchDashboard = () =>
  apiFetch<DashboardResponse>('/api/v1/dashboard');

// ─── Products ───────────────────────────────────────────────────────

export const fetchProducts = (params?: {
  integration_id?: string;
  supplier_id?: string;
  active_only?: boolean;
  limit?: number;
}) =>
  apiFetch<ProductResponse>(`/api/v1/products${buildQuery(params ?? {})}`);

export const updateProduct = (
  id: string,
  patch: {
    supplier_id?: string;
    lead_time_days?: number;
    reorder_point?: number;
    reorder_qty?: number;
    cost?: number;
    currency?: string;
    active?: boolean;
  },
) =>
  apiFetch<void>(`/api/v1/products/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    json: patch,
  });

// ─── Inventory ──────────────────────────────────────────────────────

export const fetchInventory = (params?: {
  integration_id?: string;
  limit?: number;
}) =>
  apiFetch<InventoryResponse>(`/api/v1/inventory${buildQuery(params ?? {})}`);

// ─── Suppliers ──────────────────────────────────────────────────────

export const fetchSuppliers = (params?: {
  integration_id?: string;
  limit?: number;
}) =>
  apiFetch<SupplierResponse>(`/api/v1/suppliers${buildQuery(params ?? {})}`);

export const createSupplier = (input: SupplierCreateInput) =>
  apiFetch<{ id: string }>('/api/v1/suppliers', {
    method: 'POST',
    json: input,
  });

export const updateSupplier = (id: string, patch: SupplierUpdateInput) =>
  apiFetch<void>(`/api/v1/suppliers/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    json: patch,
  });

export const deleteSupplier = (id: string) =>
  apiFetch<void>(`/api/v1/suppliers/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });

// ─── Purchase Orders ────────────────────────────────────────────────

export const fetchPurchaseOrders = (params?: {
  status?: POStatus;
  integration_id?: string;
  supplier_id?: string;
  limit?: number;
}) =>
  apiFetch<POListResponse>(`/api/v1/purchase-orders${buildQuery(params ?? {})}`);

export const fetchPurchaseOrder = (id: string) =>
  apiFetch<PODetailResponse>(`/api/v1/purchase-orders/${encodeURIComponent(id)}`);

export const sendPO = (id: string) =>
  apiFetch<void>(`/api/v1/purchase-orders/${encodeURIComponent(id)}/send`, {
    method: 'POST',
  });

export const cancelPO = (id: string) =>
  apiFetch<void>(`/api/v1/purchase-orders/${encodeURIComponent(id)}/cancel`, {
    method: 'POST',
  });

export const receivePO = (id: string) =>
  apiFetch<void>(`/api/v1/purchase-orders/${encodeURIComponent(id)}/receive`, {
    method: 'POST',
  });

// Re-export ApiError so call sites can do `instanceof ApiError`
// without an extra direct import of the kit.
export { ApiError };
